import { vocab } from '@/ds';

// Organizations in the design system's words, and the decisions an operator can take on each.

export const ORGANIZATION_TABS = ['pending', 'approved', 'suspended', 'rejected'] as const;
export type OrganizationTab = (typeof ORGANIZATION_TABS)[number];

export type ApiOrganizationStatus = 'Pending' | 'Approved' | 'Suspended' | 'Rejected';

const STATUS_API: Record<OrganizationTab, ApiOrganizationStatus> = {
  pending: 'Pending',
  approved: 'Approved',
  suspended: 'Suspended',
  rejected: 'Rejected',
};

export const statusToApi = (tab: OrganizationTab): ApiOrganizationStatus => STATUS_API[tab];

export const statusFromApi = (value: string): OrganizationTab | undefined =>
  ORGANIZATION_TABS.find((tab) => STATUS_API[tab] === value);

const term = (tab: OrganizationTab) => vocab.STATUS.organization?.[tab];

/** "Pending approval", "Approved". */
export const statusLabel = (tab: OrganizationTab): string => term(tab)?.label ?? tab;

/** "Pending" where space is tight: tabs, counts. */
export const statusShort = (tab: OrganizationTab): string =>
  term(tab)?.short ?? term(tab)?.label ?? tab;

export const isCurator = (type: string): boolean => type === 'Curator';
export const curatorTerm = vocab.ORGANIZATION_TYPES.curator;

export type DecisionKind = 'approve' | 'reject' | 'suspend' | 'reactivate';

export interface Decision {
  kind: DecisionKind;
  to: OrganizationTab;
  /** The button. A trailing ellipsis means a confirmation follows. */
  label: string;
  /** "Approved", for the toast. */
  done: string;
  /** "approve", for "Couldn't approve …". */
  verb: string;
  icon: string;
  confirm: boolean;
}

const APPROVE: Decision = {
  kind: 'approve',
  to: 'approved',
  label: 'Approve',
  done: 'Approved',
  verb: 'approve',
  icon: 'badge-check',
  confirm: false,
};

/** What each status offers, safest first. Reject and suspend confirm; the rest are undone in the toast. */
export const DECISIONS: Record<OrganizationTab, Decision[]> = {
  pending: [
    APPROVE,
    {
      kind: 'reject',
      to: 'rejected',
      label: 'Reject…',
      done: 'Rejected',
      verb: 'reject',
      icon: 'circle-x',
      confirm: true,
    },
  ],
  approved: [
    {
      kind: 'suspend',
      to: 'suspended',
      label: 'Suspend…',
      done: 'Suspended',
      verb: 'suspend',
      icon: 'circle-pause',
      confirm: true,
    },
  ],
  suspended: [
    {
      kind: 'reactivate',
      to: 'approved',
      label: 'Reactivate',
      done: 'Reactivated',
      verb: 'reactivate',
      icon: 'badge-check',
      confirm: false,
    },
  ],
  rejected: [APPROVE],
};
